import type { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import type { PostHog } from 'posthog-js';
import { store } from '../redux/store';
import { isTokenExpired } from '../utils/isTokenExpired';
import refreshTokenOrLogout from '../utils/refreshTokenOrLogout';
import { isAbortError, throwIfAborted } from '../utils/fetchCancellation';
import {
  addLearnosityBffBreadcrumb,
  captureLearnosityException,
  classifyLearnosityFailure,
  type LearnosityTelemetryFields,
} from '../utils/learnositySentry';
import fetchWithRetry from './fetchWithRetry';
import { LearnosityFetchError, readLearnosityResponse, type LearnosityFetchOperation } from './LearnosityFetchError';

/** Transient statuses retried for Learnosity startup reads, alongside transport failures. */
export const LEARNOSITY_STARTUP_RETRY_STATUSES = [408, 429, 500, 502, 503, 504] as const;

export interface LearnosityFetchOptions {
  signal?: AbortSignal;
  /** False when the SDK initializer owns the terminal report for an automatic refetch. */
  captureFailure?: boolean;
  /** False for optional reads or session lookups that also run in the background. */
  terminalStartup?: boolean;
}

interface StartupRequest<T> {
  operation: LearnosityFetchOperation;
  path: string;
  method?: 'POST';
  body?: object;
  fields: Pick<
    LearnosityTelemetryFields,
    'activityReference' | 'activityTitle' | 'assessmentState' | 'renderingType' | 'learnerId' | 'sessionId'
  >;
  validate: (value: unknown) => value is T;
}

// Shared only by startup reads; submission/save calls retain their existing policy.
export async function fetchLearnosityStartupData<T>(
  { operation, path, method, body, fields, validate }: StartupRequest<T>,
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
  options: LearnosityFetchOptions,
): Promise<T> {
  throwIfAborted(options.signal);
  addLearnosityBffBreadcrumb(operation, fields);

  try {
    let accessToken = store.getState().accessToken.value;
    if (isTokenExpired()) {
      accessToken = (await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog, options.signal)) || null;
    }
    throwIfAborted(options.signal);
    const response = await fetchWithRetry(
      `${import.meta.env.VITE_BFF_URL}${path}`,
      {
        ...(method && { method }),
        ...(body !== undefined && { body: JSON.stringify(body) }),
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        signal: options.signal,
      },
      undefined,
      undefined,
      {
        httpStatuses: LEARNOSITY_STARTUP_RETRY_STATUSES,
        onRetry: ({ attempt, delayMs, status }) =>
          addLearnosityBffBreadcrumb(operation, {
            detail: status === undefined ? 'network_retry' : 'http_retry',
            retryAttempt: attempt,
            retryDelayMs: delayMs,
            ...(status !== undefined && { status }),
          }),
      },
    );
    throwIfAborted(options.signal);
    addLearnosityBffBreadcrumb(operation, { ...fields, status: response.status });
    return await readLearnosityResponse(response, operation, validate, options.signal);
  } catch (error) {
    throwIfAborted(options.signal);
    if (isAbortError(error)) throw error;
    if (options.captureFailure !== false) {
      // Required startup reads bypass noise filters; background and optional reads use ordinary reporting.
      captureLearnosityException(error, {
        operation,
        ...fields,
        api: 'items',
        terminalStartup: options.terminalStartup ?? true,
        errorCategory: classifyLearnosityFailure(error),
        ...(error instanceof LearnosityFetchError && { status: error.status }),
      });
    }
    throw error;
  }
}
